import { Message, useMessage } from '@messageformat/react';
import { Col, Form, FormRow, Row, Tile } from '@op/opds.opux';
import { Stepper } from '@op/opds.opux-stepper';
import { space } from '@op/opds.tokens';
import { FormEvent, ReactElement, useEffect, useState } from 'react';
import ResetButton from '../Course6/TaskOne/ResetButton';
import SavingsInput from './SavingsInput';
import {
  decreaseRoundedValue,
  formatNumberWithoutDecimals,
  increaseRoundedValue,
} from '../../utils';

interface Props {
  messageId: string;
}

const getCostValues = (items: { [key: string]: () => string }): string[] => {
  const valuesArray = Object.keys(items).map((key) => {
    return items[key]();
  });
  return valuesArray;
};

const SavingsCalculator = (props: Props): ReactElement => {
  const { messageId } = props;
  const messageArray = useMessage(messageId);
  const leftColumn = getCostValues(messageArray).slice(0, 7);
  const rightColumn = getCostValues(messageArray).slice(7);
  const [amounts, setAmounts] = useState<number[]>(new Array(14).fill(0));
  const [subtractValue, setSubtractValue] = useState<number>(0);
  const [netTotal, setNetTotal] = useState<number>(0);
  const [resetPressed, setResetPressed] = useState<boolean>(false);
  const incomeLabel = useMessage('course4.question1.calculator.net_income');

  const handleAmountChange = (index: number, value: string): void => {
    const numericValue = Number(value) || 0;
    const updatedAmounts = [...amounts];
    updatedAmounts[index] = numericValue;
    setAmounts(updatedAmounts);
  };

  useEffect(() => {
    setNetTotal(
      subtractValue -
        amounts.reduce((acc, val) => {
          return acc + val;
        }, 0),
    );
  }, [subtractValue, amounts]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
  };

  return (
    <Tile
      className="investoracademy-calculator-tile investoracademy-flex-column-center"
      wrapContent={false}
    >
      <h4
        className="opds-styles-v1-text-title-small-strong"
        style={{ textAlign: 'center' }}
      >
        <Message id="course4.savingscalculator_title" />
      </h4>
      <div className="investoracademy-divider-line" />
      <Row>
        <Col style={{ display: 'block' }} xs={12} md={6}>
          <Form spacing="small">
            {leftColumn.map((label, index) => {
              return (
                <SavingsInput
                  key={label}
                  label={label}
                  value={amounts[index]}
                  onChange={(value) => {
                    handleAmountChange(index, value);
                  }}
                />
              );
            })}
          </Form>
        </Col>
        <Col style={{ display: 'block' }} xs={12} md={6}>
          <Form spacing="small">
            {rightColumn.map((label, index) => {
              return (
                <SavingsInput
                  key={label}
                  label={label}
                  value={amounts[index + 7]}
                  onChange={(value) => {
                    return handleAmountChange(index + 7, value);
                  }}
                />
              );
            })}
          </Form>
        </Col>
      </Row>
      <div className="investoracademy-divider-line" />
      <Form
        spacing="small"
        style={{ width: '100%', paddingLeft: '16px', paddingRight: '16px' }}
        onSubmit={handleSubmit}
      >
        <FormRow label={incomeLabel} className="investoracademy-calculator-income-label">
          <div className="investoracademy-stepper-container">
            <Stepper
              size="small"
              min={0}
              max={20000}
              largeStepMultiplier={100}
              unit="€"
              value={subtractValue}
              className="investoracademy-income-stepper"
              onChange={(newValue: number) => {
                return setSubtractValue(newValue);
              }}
              onClickIncrease={() => {
                setSubtractValue(increaseRoundedValue(subtractValue, 50));
              }}
              onClickDecrease={() => {
                setSubtractValue(decreaseRoundedValue(subtractValue, 50));
              }}
            />
          </div>
        </FormRow>
      </Form>
      <div className="investoracademy-divider-line" />
      <div
        className="investoracademy-calculator-item-list"
        style={{ paddingLeft: space.small, paddingRight: space.small }}
      >
        <p
          className="investoracademy-total-text investoracademy-textHighlight-small"
          aria-live="polite"
        >
          <Message
            id={
              resetPressed && !netTotal
                ? 'course4.question1.reset_text'
                : 'course4.question1.savings'
            }
          />
        </p>
        <p
          data-testid="savings-total"
          className="investoracademy-number-highlightLarge"
          style={{ width: '160px', textAlign: 'right', paddingBottom: '0px' }}
        >
          {netTotal === 0 ? '-' : formatNumberWithoutDecimals(netTotal)} €
        </p>
      </div>
      <div className="investoracademy-divider-line" />
      <ResetButton
        onClick={() => {
          setAmounts(new Array(14).fill(0));
          setSubtractValue(0);
          setResetPressed(true);
        }}
      />
    </Tile>
  );
};

export default SavingsCalculator;
